import React, { useCallback, useId, useRef, useState } from "react";
import {
    AutocompleteInputChangeReason,
    AutocompleteCloseReason,
    AutocompleteRenderGroupParams,
    AutocompleteChangeReason,
    AutocompleteRenderGetTagProps,
    AutocompleteRenderInputParams,
    ButtonBase,
    Chip,
    ChipProps,
    InputAdornment,
    Paper,
    PaperProps,
    TextField,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowRight, SearchOutlined } from "@mui/icons-material";

export interface SearchableSelectOption<T> {
    value: T;
    label: string;
}

// Converts a value->label dictionary into the option list shape used by the searchable selects
export function optionsFromDict<T extends string>(dict: { [key in T]: string }): SearchableSelectOption<T>[] {
    return Object.entries(dict).map(([key, label]) => ({ value: key as T, label: label as string }));
}

// Returns a new list sorted case-insensitively by label, for predictable dropdown ordering
export function sortOptionsAlphabetically<T>(options: SearchableSelectOption<T>[]): SearchableSelectOption<T>[] {
    return [...options].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: "base" }));
}

// Compares two options by value (string-coerced), used as MUI's isOptionEqualToValue
export function optionValuesEqual<T>(a: SearchableSelectOption<T>, b: SearchableSelectOption<T>): boolean {
    return String(a.value) === String(b.value);
}

// Resolves a single stored value to its matching option, or null if absent, for single-selects
export function findOption<T>(options: SearchableSelectOption<T>[], value: T | null): SearchableSelectOption<T> | null {
    if (value === null) {
        return null;
    }
    return options.find(x => String(x.value) === String(value)) || null;
}

// Resolves a list of stored values to their matching options for multi-selects, dropping unknowns
export function findOptions<T>(options: SearchableSelectOption<T>[], values: T[]): SearchableSelectOption<T>[] {
    return values
        .map(v => findOption(options, v))
        .filter((option): option is SearchableSelectOption<T> => option !== null);
}

// Appends synthetic options for selected values missing from `options`, so unknown/deleted
// references stay visible (as their raw value) instead of silently disappearing
export function withUnknownValues<T>(options: SearchableSelectOption<T>[], selected: T[]): SearchableSelectOption<T>[] {
    const unknown = selected
        .filter(v => !options.some(x => String(x.value) === String(v)))
        .map(v => ({ value: v, label: String(v) }));
    return unknown.length ? [...options, ...unknown] : options;
}

// Wraps the portions of `text` matching `query` in a highlight element (bold by default),
// used to emphasize the search term within rendered option labels
export function highlightMatch(
    text: string,
    query: string,
    renderMatch: (part: string, key: number) => React.ReactNode = (part, key) => React.createElement("b", { key }, part),
): React.ReactNode {
    if (!query) {
        return text;
    }

    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const parts = text.split(new RegExp(`(${escaped})`, "i"));

    return React.createElement("span", null, ...parts.map((part, i) => part.toLowerCase() === query.toLowerCase()
        ? renderMatch(part, i)
        : React.createElement(React.Fragment, { key: i }, part)));
}

// Renders the Autocomplete's input as a search-styled TextField (with magnifier adornment),
// used as the renderInput for the searchable selects
export function searchTextField(
    params: AutocompleteRenderInputParams,
    opts: {
        label?: string;
        error?: boolean;
        placeholder?: string;
        hideSearchIcon?: boolean;
        groupToggleInputBlur?: (
            event: React.FocusEvent<HTMLInputElement>,
            autocompleteOnBlur?: React.FocusEventHandler<HTMLInputElement>,
        ) => void;
    } = {},
): JSX.Element {
    return <TextField
        {...params}
        label={opts.label}
        error={opts.error}
        placeholder={opts.placeholder || (opts.hideSearchIcon ? undefined : "Type to search")}
        inputProps={{
            ...params.inputProps,
            onKeyDown: opts.groupToggleInputBlur
                ? (event) => {
                    if (event.key === "Tab" && !event.shiftKey) {
                        const listboxId = event.currentTarget.getAttribute("aria-controls");
                        const firstGroupToggle = listboxId
                            ? document.getElementById(listboxId)?.querySelector<HTMLButtonElement>("[data-collapsible-group-toggle='true']")
                            : null;
                        if (firstGroupToggle) {
                            event.preventDefault();
                            firstGroupToggle.focus();
                            return;
                        }
                    }
                    params.inputProps.onKeyDown?.(event as React.KeyboardEvent<HTMLInputElement>);
                }
                : params.inputProps.onKeyDown,
            onBlur: opts.groupToggleInputBlur
                ? (event) => {
                    const inputEvent = event as React.FocusEvent<HTMLInputElement>;
                    if ((inputEvent.relatedTarget as HTMLElement | null)?.dataset?.collapsibleGroupToggle === "true") {
                        opts.groupToggleInputBlur?.(inputEvent, params.inputProps.onBlur);
                        return;
                    }
                    params.inputProps.onBlur?.(inputEvent);
                }
                : params.inputProps.onBlur,
        }}
        InputProps={{
            ...params.InputProps,
            startAdornment: opts.hideSearchIcon ? params.InputProps.startAdornment : (
                <>
                    <InputAdornment position="start"><SearchOutlined /></InputAdornment>
                    {params.InputProps.startAdornment}
                </>
            ),
        }}
    />;
}

// Renders a dropdown option <li> with the current query highlighted in its label,
// used as the Autocomplete renderOption for search-highlighted lists
export function renderOptionWithHighlight<T>(
    liProps: React.HTMLAttributes<HTMLLIElement>,
    option: SearchableSelectOption<T>,
    state: { inputValue: string },
    disableClick = false,
    selectedLabel?: string,
): JSX.Element {
    const query = selectedLabel !== undefined && state.inputValue === selectedLabel ? "" : state.inputValue;
    return <li {...liProps} onClick={disableClick ? undefined : liProps.onClick} key={String(option.value)}>
        {highlightMatch(option.label, query)}
    </li>;
}

// Renders selected values as removable MUI Chips, used as the multi-select's renderTags
export function renderOptionChips<T>(
    value: SearchableSelectOption<T>[],
    getTagProps: AutocompleteRenderGetTagProps,
    chipColor: ChipProps["color"] = "primary",
    getLabel: (option: SearchableSelectOption<T>) => string = (option) => option.label,
): JSX.Element[] {
    return value.map((option, index) => (
        <Chip
            variant="outlined"
            color={chipColor}
            label={getLabel(option)}
            {...getTagProps({ index })}
            key={String(option.value)}
        />
    ));
}

// Detects the implicit Backspace-removes-last-chip gesture so onChange handlers can ignore it.
// In a multiple Autocomplete, Backspace in the empty input silently removes the
// last chip; onChange handlers early-return on this to keep removal explicit
export function isChipKeyboardRemoval(event: React.SyntheticEvent, reason: AutocompleteChangeReason): boolean {
    return reason === "removeOption" && event.type === "keydown"
        && (event.target as HTMLElement).tagName === "INPUT";
}

// Custom Autocomplete Paper that renders an optional pinned footer below the option list.
// PaperComponent must stay a stable reference across renders - MUI mounts it via a
// dynamic `as` prop, so a new function identity forces the open dropdown to
// unmount/remount. The footer content flows through slotProps.paper (a prop, safe
// to rebuild each render); pass it with footerSlotProps below
export function PaperWithFooter({ footer, children, sx, ...paperProps }: PaperProps & { footer?: React.ReactNode }): JSX.Element {
    return <Paper
        {...paperProps}
        sx={{
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "& .MuiAutocomplete-listbox": { paddingBottom: "0px" },
            ...sx,
        }}
    >
        {children}
        {footer && <div onMouseDown={(e) => e.preventDefault()}>{footer}</div>}
    </Paper>;
}

// Builds the slotProps.paper payload that passes footer content to PaperWithFooter
export function footerSlotProps(footer: React.ReactNode): { paper: PaperProps } {
    return { paper: { footer } as PaperProps };
}

export const groupedSectionSlotProps: { paper: PaperProps } = {
    paper: {
        sx: {
            "& .MuiAutocomplete-groupLabel": {
                backgroundColor: "custom.surfaceSection",
                color: "text.primary",
                fontWeight: 700,
                borderBottomWidth: "1px",
                borderBottomStyle: "solid",
                borderBottomColor: "custom.borderStrong",
            },
        },
    },
};

interface CollapsibleAutocompleteGroupProps extends Omit<AutocompleteRenderGroupParams, "key"> {
    collapsed: boolean;
    panelId: string;
    onBlur: React.FocusEventHandler<HTMLButtonElement>;
    onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
    onToggle: (group: string) => void;
}

export function collapsibleAutocompleteGroupKey(id: string, label: string): string {
    return JSON.stringify([id, label]);
}

function collapsibleAutocompleteGroupLabel(group: string): string {
    try {
        const parsed = JSON.parse(group);
        return Array.isArray(parsed) && typeof parsed[1] === "string" ? parsed[1] : group;
    } catch {
        return group;
    }
}

function CollapsibleAutocompleteGroup(props: CollapsibleAutocompleteGroupProps): JSX.Element {
    const groupLabel = collapsibleAutocompleteGroupLabel(props.group);
    return <li>
        <div className="MuiAutocomplete-groupLabel">
            <ButtonBase
                component="button"
                type="button"
                aria-controls={props.panelId}
                aria-expanded={!props.collapsed}
                aria-label={`${props.collapsed ? "Expand" : "Collapse"} ${groupLabel}`}
                data-collapsible-group-toggle="true"
                onMouseDown={(event) => event.preventDefault()}
                onBlur={props.onBlur}
                onKeyDown={props.onKeyDown}
                onClick={() => props.onToggle(props.group)}
                sx={{
                    color: "inherit",
                    font: "inherit",
                    justifyContent: "flex-start",
                    lineHeight: "inherit",
                    textAlign: "left",
                    width: "100%",
                }}
            >
                {props.collapsed ? <KeyboardArrowRight fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                {groupLabel}
            </ButtonBase>
        </div>
        <ul className="MuiAutocomplete-groupUl" id={props.panelId}>
            {props.collapsed ? null : props.children}
        </ul>
    </li>;
}

export interface CollapsibleAutocompleteGroups {
    onClose: (event: React.SyntheticEvent, reason: AutocompleteCloseReason) => void;
    onInputChange: (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => void;
    onInputBlurToGroupToggle: (
        event: React.FocusEvent<HTMLInputElement>,
        autocompleteOnBlur?: React.FocusEventHandler<HTMLInputElement>,
    ) => void;
    onOpen: () => void;
    open: boolean;
    renderGroup: (params: AutocompleteRenderGroupParams) => React.ReactNode;
}

// Keeps directory sections independently collapsible. A live search temporarily
// expands every matching section without changing the user's collapsed choices.
export function useCollapsibleAutocompleteGroups(): CollapsibleAutocompleteGroups {
    const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
    const [open, setOpen] = useState(false);
    const isSearching = useRef(false);
    const pendingInputBlurRef = useRef<{
        input: HTMLInputElement;
        complete: (() => void) | null;
    } | null>(null);
    const idPrefix = useId().replace(/:/g, "");

    const toggleGroup = useCallback((group: string) => {
        setCollapsedGroups(current => {
            const next = new Set(current);
            if (next.has(group)) {
                next.delete(group);
            } else {
                next.add(group);
            }
            return next;
        });
    }, []);

    const onInputChange = useCallback((_event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
        isSearching.current = reason === "input" && value.length > 0;
    }, []);

    const onOpen = useCallback(() => {
        pendingInputBlurRef.current = null;
        setOpen(true);
    }, []);

    const onClose = useCallback((_event: React.SyntheticEvent, _reason: AutocompleteCloseReason) => {
        setOpen(false);
    }, []);

    const onInputBlurToGroupToggle = useCallback((
        event: React.FocusEvent<HTMLInputElement>,
        autocompleteOnBlur?: React.FocusEventHandler<HTMLInputElement>,
    ) => {
        pendingInputBlurRef.current = {
            input: event.currentTarget,
            complete: autocompleteOnBlur ? () => autocompleteOnBlur(event) : null,
        };
    }, []);

    const onGroupBlur = useCallback((event: React.FocusEvent<HTMLButtonElement>) => {
        const listbox = event.currentTarget.closest("[role='listbox']");
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && listbox?.contains(nextTarget)) {
            return;
        }

        const pendingInputBlur = pendingInputBlurRef.current;
        pendingInputBlurRef.current = null;
        if (nextTarget === pendingInputBlur?.input) {
            return;
        }

        if (pendingInputBlur?.complete) {
            pendingInputBlur.complete();
        } else {
            setOpen(false);
        }
    }, []);

    const onGroupKeyDown = useCallback((event: React.KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== "Escape") {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        const input = pendingInputBlurRef.current?.input;
        input?.focus();
        pendingInputBlurRef.current = null;
        setOpen(false);
    }, []);

    const renderGroup = useCallback((params: AutocompleteRenderGroupParams) => {
        const collapsed = !isSearching.current && collapsedGroups.has(params.group);
        return <CollapsibleAutocompleteGroup
            key={params.key}
            group={params.group}
            children={params.children}
            collapsed={collapsed}
            panelId={`${idPrefix}-group-${params.key}`}
            onBlur={onGroupBlur}
            onKeyDown={onGroupKeyDown}
            onToggle={toggleGroup}
        />;
    }, [collapsedGroups, idPrefix, onGroupBlur, onGroupKeyDown, toggleGroup]);

    return { onClose, onInputBlurToGroupToggle, onInputChange, onOpen, open, renderGroup };
}
